import { Constraint } from "types/constraintType";
import { LineTapConstraint } from "types/lineConstraintType";
import {
    CAPACITY_RESOURCE_TYPE,
    CHARGING_RESOURCE_TYPE,
    ENERGY_RESOURCE_TYPE
} from "types/resourceType";
import { PowerAmounts } from "../../BusDetailPane/BusDetailPane";
import {
    getAllocatedCostForConstraint,
    getRelevantConstraintsForSize
} from "../../BusDetailPane/constraintsHelpers";

export type GraphType = "discharging" | "charging";

export type ConstraintType = Constraint | LineTapConstraint;

export type CapacityCostInput = {
    readonly energy_size: number;
    readonly capacity_size?: number;
    readonly charging_size?: number;
};

export type CapacityCostDataPoint = {
    readonly xValue: number;
    readonly energySize: number;
    readonly capacitySize: number;
    readonly chargingSize: number;
    readonly constraints: number;
    readonly cost: number;
    readonly costPerMW: number;
    readonly allocatedCost: number;
    readonly allocatedCostPerMW: number;
    readonly totalCost: number;
};

// Cost formatting constants
export const THOUSAND = 1000;
export const MILLION = 1000000;

// Reference line styling constants
export const STROKE_DASH_ARRAY = "3 3";
export const STROKE_WIDTH = 1;
export const LABEL_FONT_SIZE = 10;
export const REFERENCE_LINE_COLOR = "rgb(235, 166, 81)";

// Y-axis range constant
const Y_RANGE_PADDING = 1.1;

// Image export constants
const PNG_EXPORT_SCALE = 3;
const DEFAULT_CHART_WIDTH = 800;
const DEFAULT_CHART_HEIGHT = 300;
const EXPORT_LABEL_HEIGHT = 30;
const EXPORT_LABEL_FONT_SIZE = 12;
const EXPORT_LABEL_COLOR = "#666";
const EXPORT_LABEL_Y_OFFSET = 20;

// Logo constants
const LOGO_WIDTH = 60;
const LOGO_HEIGHT = 18;
const LOGO_RIGHT_MARGIN = 25;
const LOGO_BOTTOM_MARGIN = 10;
const LOGO_OPACITY = 0.3;

const NIRA_LOGO_SVG = `<svg width="158" height="47" viewBox="0 0 158 47" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M89.233 11.0909H81.8864V33.0455H81.5795L66.5114 11.0909H60.0341V46H67.4148V24.0284H67.6705L82.858 46H89.233V11.0909ZM95.1946 46H102.456V19.8182H95.1946V46ZM98.8423 16.4432C101.007 16.4432 102.78 14.7898 102.78 12.7614C102.78 10.75 101.007 9.09659 98.8423 9.09659C96.6946 9.09659 94.9219 10.75 94.9219 12.7614C94.9219 14.7898 96.6946 16.4432 98.8423 16.4432ZM108.273 46H115.534V31.1875C115.534 27.9659 117.886 25.75 121.091 25.75C122.097 25.75 123.477 25.9205 124.159 26.142V19.6989C123.511 19.5455 122.608 19.4432 121.875 19.4432C118.943 19.4432 116.54 21.1477 115.585 24.3864H115.312V19.8182H108.273V46ZM134.629 46.4943C138.499 46.4943 141.004 44.8068 142.283 42.3693H142.487V46H149.374V28.3409C149.374 22.1023 144.089 19.4773 138.26 19.4773C131.987 19.4773 127.862 22.4773 126.857 27.25L133.572 27.7955C134.067 26.0568 135.618 24.7784 138.226 24.7784C140.697 24.7784 142.112 26.0227 142.112 28.1705V28.2727C142.112 29.9602 140.322 30.1818 135.771 30.625C130.589 31.1023 125.936 32.8409 125.936 38.6875C125.936 43.8693 129.635 46.4943 134.629 46.4943ZM136.709 41.483C134.476 41.483 132.874 40.4432 132.874 38.4489C132.874 36.4034 134.561 35.3977 137.118 35.0398C138.703 34.8182 141.294 34.4432 142.163 33.8636V36.642C142.163 39.3864 139.896 41.483 136.709 41.483Z" fill="#394B59"/>
<circle cx="36" cy="10" r="10" fill="#F7A23B"/>
<path d="M0 34.5769H24.2306V39.7692H0V34.5769Z" fill="#2FB0FF"/>
<path d="M0 41.5H38.0766V46.6923H0V41.5Z" fill="#2FB0FF"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M25.9614 32.8461C25.9614 33.802 25.1865 34.5769 24.2306 34.5769C24.2259 34.5769 24.2211 34.5769 24.2163 34.5768H17.5257C18.2942 37.5628 21.0048 39.7691 24.2306 39.7691C28.0541 39.7691 31.1537 36.6696 31.1537 32.8461C31.1537 29.0226 28.0541 25.9231 24.2306 25.9231C20.4072 25.9231 17.3076 29.0226 17.3076 32.8461H22.4999C22.4999 31.8902 23.2748 31.1154 24.2306 31.1154C25.1865 31.1154 25.9614 31.8902 25.9614 32.8461Z" fill="#2FB0FF"/>
<path d="M0 27.6538H10.3845V32.8461H0V27.6538Z" fill="#2FB0FF"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.1157 25.923C12.1157 26.8789 11.3408 27.6538 10.3849 27.6538C10.3843 27.6538 10.3837 27.6538 10.3831 27.6538H3.68002C4.44855 30.6397 7.15909 32.846 10.3849 32.846C14.2084 32.846 17.308 29.7465 17.308 25.923C17.308 22.0995 14.2084 19 10.3849 19C6.56146 19 3.46191 22.0995 3.46191 25.923H8.65418C8.65418 24.9672 9.42907 24.1923 10.3849 24.1923C11.3408 24.1923 12.1157 24.9672 12.1157 25.923Z" fill="#2FB0FF"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M39.8081 39.7692C39.8081 40.7251 39.0332 41.5 38.0773 41.5C38.077 41.5 38.0767 41.5 38.0765 41.5V41.5001H31.3724C32.141 44.486 34.8515 46.6922 38.0773 46.6922C41.9008 46.6922 45.0003 43.5927 45.0003 39.7692C45.0003 35.9457 41.9008 32.8462 38.0773 32.8462C34.2538 32.8462 31.1543 35.9457 31.1543 39.7692C31.1543 39.7693 31.1543 39.7693 31.1543 39.7693H36.3466C36.3466 39.7693 36.3466 39.7693 36.3466 39.7692C36.3466 38.8133 37.1214 38.0385 38.0773 38.0385C39.0332 38.0385 39.8081 38.8133 39.8081 39.7692Z" fill="#2FB0FF"/>
</svg>`;

const NIRA_LOGO_DATA_URL = `data:image/svg+xml;base64,${btoa(NIRA_LOGO_SVG)}`;

const generateExportFileName = (
    exportName: string | undefined,
    exportId: string,
    graphType: GraphType,
    extension: "csv" | "png"
): string => {
    const suffix = graphType === "charging" ? "_charging" : "";
    const base = exportName ? `${exportName}_${exportId}` : exportId;
    return `${base}_nira_costs${suffix}.${extension}`;
};

export const formatCostValue = (value: number): string => {
    const absValue = Math.abs(value);
    // Use M for values >= 1M, K for values < 1M
    if (absValue >= MILLION) {
        const rounded = Math.round(value / MILLION);
        return `$${rounded.toLocaleString()}M`;
    }

    const thousands = value / THOUSAND;
    const rounded = Math.round(thousands);
    return `$${rounded.toLocaleString()}K`;
};

export const formatCostLabel = (
    showPerMW: boolean,
    projectSize: number,
    costPerMW: number | undefined,
    totalCost: number | undefined
): string => {
    if (showPerMW && costPerMW !== undefined) {
        // Format the cost value intelligently (K or M based on magnitude)
        const formattedValue = formatCostValue(costPerMW);
        return `Cost at ${projectSize}MW: ${formattedValue}/MW`;
    }
    if (!showPerMW && totalCost !== undefined) {
        // Format the cost value intelligently (K or M based on magnitude)
        const formattedValue = formatCostValue(totalCost);
        return `Cost at ${projectSize}MW: ${formattedValue}`;
    }
    return "";
};

const createCostDataPoint = (
    constraints: ReadonlyArray<ConstraintType>,
    powerAmounts: PowerAmounts,
    hasAllocatedCost: boolean,
    xValue: number,
    sizeForPerMW: number
): CapacityCostDataPoint => {
    const filteredConstraints = getRelevantConstraintsForSize(
        constraints,
        powerAmounts
    );

    const { allocatedCost, totalCost } = calculateCostsFromConstraints(
        filteredConstraints,
        powerAmounts
    );

    const cost = hasAllocatedCost ? allocatedCost : totalCost;

    return {
        xValue,
        energySize: powerAmounts.energySize,
        capacitySize: powerAmounts.capacitySize,
        chargingSize: powerAmounts.chargingSize,
        constraints: filteredConstraints.length,
        cost,
        costPerMW: sizeForPerMW > 0 ? cost / sizeForPerMW : 0,
        allocatedCost,
        allocatedCostPerMW: sizeForPerMW > 0 ? allocatedCost / sizeForPerMW : 0,
        totalCost
    };
};

export const calculateDischargingCosts = (
    constraints: ReadonlyArray<ConstraintType>,
    hasAllocatedCost: boolean,
    capacityCosts: ReadonlyArray<CapacityCostInput>,
    energyOnly: boolean
): CapacityCostDataPoint[] => {
    return capacityCosts.map((capacityCost) => {
        const energySize = capacityCost.energy_size;
        const capacitySize = energyOnly ? 0 : capacityCost.capacity_size ?? 0;
        const chargingSize = capacityCost.charging_size ?? 0;
        const powerAmounts: PowerAmounts = {
            energySize,
            capacitySize,
            chargingSize
        };

        return createCostDataPoint(
            constraints,
            powerAmounts,
            hasAllocatedCost,
            energySize,
            energySize
        );
    });
};

export const calculateChargingCosts = (
    constraints: ReadonlyArray<ConstraintType>,
    hasAllocatedCost: boolean,
    capacityCosts: ReadonlyArray<CapacityCostInput>
): CapacityCostDataPoint[] => {
    return capacityCosts.map((capacityCost) => {
        const chargingSize = capacityCost.charging_size ?? 0;
        const powerAmounts: PowerAmounts = {
            energySize: 0,
            capacitySize: 0,
            chargingSize
        };

        return createCostDataPoint(
            constraints,
            powerAmounts,
            hasAllocatedCost,
            chargingSize,
            chargingSize
        );
    });
};

const calculateCostsFromConstraints = (
    constraints: ReadonlyArray<ConstraintType>,
    powerAmounts: PowerAmounts
): { allocatedCost: number; totalCost: number } => {
    // Calculate allocated costs for each constraint
    const constraintsWithCosts = constraints.map((constraint) => ({
        constraint,
        allocatedCost: getAllocatedCostForConstraint(constraint, powerAmounts)
    }));

    // Sort by allocated cost descending for deduplication
    const sorted = [...constraintsWithCosts].sort(
        (a, b) => b.allocatedCost - a.allocatedCost
    );

    // Dedupe by monitoredFacilityId, keeping highest allocated cost
    const seenFacilities = new Set<string>();
    const deduped = sorted.filter(({ constraint }) => {
        if (seenFacilities.has(constraint.monitoredFacilityId)) {
            return false;
        }
        seenFacilities.add(constraint.monitoredFacilityId);
        return true;
    });

    const allocatedCost = deduped.reduce(
        (sum, item) => sum + item.allocatedCost,
        0
    );
    const totalCost = deduped.reduce(
        (sum, item) => sum + (item.constraint.estimatedCost ?? 0),
        0
    );

    return { allocatedCost, totalCost };
};

export const filterDischargingConstraints = (
    constraints: ReadonlyArray<ConstraintType>
): ConstraintType[] => {
    return constraints.filter(
        (constraint) =>
            constraint.resourceType === ENERGY_RESOURCE_TYPE ||
            constraint.resourceType === CAPACITY_RESOURCE_TYPE
    );
};

export const filterChargingConstraints = (
    constraints: ReadonlyArray<ConstraintType>
): ConstraintType[] => {
    return constraints.filter(
        (constraint) => constraint.resourceType === CHARGING_RESOURCE_TYPE
    );
};

export const interpolateCostAtSize = (
    sortedChartData: CapacityCostDataPoint[],
    targetSize: number
): { cost: number; costPerMW: number } | undefined => {
    if (sortedChartData.length === 0) {
        return undefined;
    }

    // Find exact match
    const exactPoint = sortedChartData.find((d) => d.xValue === targetSize);
    if (exactPoint) {
        return { cost: exactPoint.cost, costPerMW: exactPoint.costPerMW };
    }

    // Linear interpolation - data is already sorted by xValue
    // Find lower point (last point where xValue < targetSize)
    let lowerPoint: CapacityCostDataPoint | undefined;
    let upperPoint: CapacityCostDataPoint | undefined;

    for (const point of sortedChartData) {
        if (point.xValue < targetSize) {
            lowerPoint = point;
        } else if (point.xValue > targetSize) {
            upperPoint = point;
            break; // Found first point above target, stop
        }
    }

    if (lowerPoint && upperPoint) {
        const ratio =
            (targetSize - lowerPoint.xValue) /
            (upperPoint.xValue - lowerPoint.xValue);
        return {
            cost: lowerPoint.cost + ratio * (upperPoint.cost - lowerPoint.cost),
            costPerMW:
                lowerPoint.costPerMW +
                ratio * (upperPoint.costPerMW - lowerPoint.costPerMW)
        };
    } else if (lowerPoint) {
        return { cost: lowerPoint.cost, costPerMW: lowerPoint.costPerMW };
    } else if (upperPoint) {
        return { cost: upperPoint.cost, costPerMW: upperPoint.costPerMW };
    }

    return undefined;
};

export const downloadCSV = (
    chartData: CapacityCostDataPoint[],
    graphType: GraphType,
    showCapacity: boolean,
    exportName: string | undefined,
    exportId: string
): void => {
    // Dynamic first column based on graph type
    const xAxisLabel =
        graphType === "charging"
            ? "Project Charging Size (MW)"
            : "Project Energy Size (MW)";

    const headers = [xAxisLabel];
    if (showCapacity) {
        headers.push("Capacity Size (MW)");
    }
    headers.push(
        "Constraints",
        "Allocated Cost ($)",
        "Allocated Cost per MW ($/MW)",
        "Total Cost ($)"
    );

    const csvContent = [
        headers,
        ...chartData.map((d) => {
            // Use xValue which is the correct x-axis value for both graph types
            const row: (string | number)[] = [d.xValue];
            if (showCapacity) {
                row.push(d.capacitySize);
            }
            row.push(
                d.constraints,
                d.allocatedCost.toFixed(2),
                d.allocatedCostPerMW.toFixed(2),
                d.totalCost.toFixed(2)
            );
            return row;
        })
    ]
        .map((row) => row.join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = generateExportFileName(
        exportName,
        exportId,
        graphType,
        "csv"
    );
    link.click();
    URL.revokeObjectURL(url);
};

export const downloadImage = (
    chartElement: HTMLDivElement | null,
    exportName: string | undefined,
    exportId: string,
    graphType: GraphType,
    exportLabel?: string
): void => {
    if (!chartElement) return;

    const svgElement = chartElement.querySelector("svg");
    if (!svgElement) return;

    // Use getBoundingClientRect for reliable dimensions (img.width/height can be 0 for SVG blobs)
    const rect = svgElement.getBoundingClientRect();
    const width = rect.width || svgElement.clientWidth || DEFAULT_CHART_WIDTH;
    const height =
        rect.height || svgElement.clientHeight || DEFAULT_CHART_HEIGHT;

    // Add extra height for export label if provided
    const labelHeight = exportLabel ? EXPORT_LABEL_HEIGHT : 0;
    const totalHeight = height + labelHeight;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clone SVG and set explicit dimensions to ensure proper rendering
    const svgClone = svgElement.cloneNode(true) as SVGElement;
    svgClone.setAttribute("width", String(width));
    svgClone.setAttribute("height", String(height));

    const svgString = new XMLSerializer().serializeToString(svgClone);
    const chartImg = new Image();
    const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8"
    });
    const url = URL.createObjectURL(svgBlob);

    // Load logo image from data URL
    const logoImg = new Image();
    logoImg.src = NIRA_LOGO_DATA_URL;

    chartImg.onload = () => {
        canvas.width = width * PNG_EXPORT_SCALE;
        canvas.height = totalHeight * PNG_EXPORT_SCALE;
        ctx.scale(PNG_EXPORT_SCALE, PNG_EXPORT_SCALE);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, totalHeight);
        ctx.drawImage(chartImg, 0, 0, width, height);

        // Draw logo in bottom right of chart area
        const logoX = width - LOGO_WIDTH - LOGO_RIGHT_MARGIN;
        const logoY = height - LOGO_HEIGHT - LOGO_BOTTOM_MARGIN;

        // Draw logo if loaded, otherwise continue without it
        if (logoImg.complete && logoImg.naturalWidth > 0) {
            ctx.globalAlpha = LOGO_OPACITY;
            ctx.drawImage(logoImg, logoX, logoY, LOGO_WIDTH, LOGO_HEIGHT);
            ctx.globalAlpha = 1;
        }

        // Draw export label at the bottom center
        if (exportLabel) {
            ctx.fillStyle = EXPORT_LABEL_COLOR;
            ctx.font = `${EXPORT_LABEL_FONT_SIZE}px sans-serif`;
            ctx.textAlign = "center";
            ctx.fillText(
                exportLabel,
                width / 2,
                height + EXPORT_LABEL_Y_OFFSET
            );
        }

        canvas.toBlob((blob) => {
            if (blob) {
                const pngUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = pngUrl;
                link.download = generateExportFileName(
                    exportName,
                    exportId,
                    graphType,
                    "png"
                );
                link.click();
                URL.revokeObjectURL(pngUrl);
            }
        });
        URL.revokeObjectURL(url);
    };

    chartImg.src = url;
};

export const calculateYRange = (
    data: CapacityCostDataPoint[],
    showPerMW: boolean
): [number, number] => {
    if (data.length === 0) {
        return [0, 0];
    }

    if (showPerMW) {
        const min = Math.min(...data.map((d) => d.costPerMW));
        const max = Math.max(...data.map((d) => d.costPerMW)) * Y_RANGE_PADDING;
        return [min, max];
    }

    const min = Math.min(...data.map((d) => d.cost));
    const max = Math.max(...data.map((d) => d.cost)) * Y_RANGE_PADDING;
    return [min, max];
};

export const calculateXBounds = (
    data: CapacityCostDataPoint[]
): { min: number; max: number } => {
    if (data.length === 0) {
        return { min: 0, max: 0 };
    }
    const values = data.map((d) => d.xValue);
    return { min: Math.min(...values), max: Math.max(...values) };
};

export const formatScopeViewType = (scopeView: string): string => {
    const parts = scopeView.split("_");
    const fuelType = parts[parts.length - 1];
    return fuelType.charAt(0).toUpperCase() + fuelType.slice(1);
};

export const formatExportDate = (): string => {
    const now = new Date();
    return `${now.getMonth() + 1}/${now.getDate()}/${String(
        now.getFullYear()
    ).slice(-2)}`;
};
